// Varelinjer med søk i /produkter, manuell vare og kalkyle (tilbud/kalkyle.js). Felles for
// pristilbudet (EditOffer) og pris på en ordre (OrdrePris), så de ser og regner likt.
// Med arbeidsliste (bestillinger i klubbinfo, vinnsel 04.11) kan det også legges til trykk og annet
// arbeid, fra prislisten for tekstiltrykk eller eget, som på trykksiden (trykk/TrykkLinjer.jsx).
// Linjene får da type «arbeid» eller «produkt», som følger med til ordren og trykket.
import React, { useState } from 'react';
import ProduktSok from '../components/ProduktSok';
import { Tekstfelt, Valgfelt, knappSekundar, feltKlasse } from '../ny-kundesamhandling/Skjema';
import Ikon from '../ny-kundesamhandling/Ikon';
import { linjeTall, medRabatt, regnPaNytt, harKostpris, kr, prosent } from './kalkyle';

import { renVarekode as renEan, varekodeTekst } from './varekode';

// Lite tallfelt i en linje, med synlig etikett.
export function Tall({ id, etikett, value, onChange, enhet, bredde = 'w-28', disabled = false }) {
  return (
    <div className="min-w-0">
      <label htmlFor={id} className="mb-1 block text-sm font-medium text-slate-700">{etikett}</label>
      <div className="relative">
        <input
          id={id}
          type="text"
          inputMode="decimal"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className={`${bredde} max-w-full rounded-lg border-2 border-slate-300 bg-white disabled:bg-slate-100 disabled:text-slate-700 px-3 py-1.5 ${enhet ? 'pr-9' : ''} focus:border-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-600/30`}
        />
        {enhet && <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-600">{enhet}</span>}
      </div>
    </div>
  );
}

function Linje({ l, id, onEndre, onFjern, laast, visVerksted }) {
  const t = linjeTall(l);
  const arbeid = l.type === 'arbeid';
  return (
    <li className="space-y-2 rounded-xl border-2 border-slate-200 bg-white p-2.5">
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-0 flex-1 basis-60">
          <label htmlFor={`${id}-art`} className="mb-1 flex items-center gap-2 text-sm font-medium text-slate-700">
            {arbeid ? <><span className="rounded-full bg-violet-100 px-2 py-0.5 text-xs font-semibold text-violet-900">Arbeid</span> Trykk eller arbeid</> : 'Vare'}
          </label>
          <input id={`${id}-art`} value={l.artikkel || ''} onChange={(e) => onEndre('artikkel', e.target.value)} disabled={laast} className={`${feltKlasse} py-1.5 text-base`} />
        </div>
        <p className="shrink-0 text-right">
          <span className="block text-sm text-slate-600">Linjesum</span>
          <span className="block text-lg font-bold text-slate-900">{kr(t.total)}</span>
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Tall id={`${id}-pris`} etikett="Pris inkl. mva" enhet="kr" value={l.pris} onChange={(v) => onEndre('pris', v)} disabled={laast} />
        <Tall id={`${id}-ant`} etikett="Antall" value={l.antall} onChange={(v) => onEndre('antall', v)} bredde="w-20" disabled={laast} />
        <Tall id={`${id}-rab`} etikett="Rabatt i %" enhet="%" value={l.rabatt} onChange={(v) => onEndre('rabatt', v)} bredde="w-24" disabled={laast} />
        <Tall id={`${id}-rabkr`} etikett="Rabatt i kr" enhet="kr" value={l.rabattKr} onChange={(v) => onEndre('rabattKr', v)} disabled={laast} />
        <Tall id={`${id}-kost`} etikett="Kostpris eks. mva" enhet="kr" value={harKostpris(l) ? l.kostpris : ''} onChange={(v) => onEndre('kostpris', v)} disabled={laast} />
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm">
        {t.bidrag ? (
          <span className={t.bidrag.bidrag < 0 ? 'font-semibold text-red-700' : 'text-slate-700'}>
            Dekningsbidrag {kr(t.bidrag.bidrag)}{t.bidrag.dg != null ? ` · ${prosent(t.bidrag.dg)}` : ''}
          </span>
        ) : (
          <span className="font-medium text-amber-800">Dekningsbidrag: kostpris mangler</span>
        )}
        {arbeid ? null : laast ? (
          l.ean && <span className="text-slate-600">{varekodeTekst(l.ean)}</span>
        ) : (
          <span className="inline-flex items-center gap-2">
            <label htmlFor={`${id}-ean`} className="font-medium text-slate-700">EAN/PLU</label>
            <input id={`${id}-ean`} type="text" autoComplete="off" value={l.ean || ''}
              onChange={(e) => onEndre('ean', renEan(e.target.value))}
              className="w-48 max-w-full rounded-lg border-2 border-slate-300 bg-white px-2 py-1 font-mono focus:border-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-600/30" />
          </span>
        )}
        {/* Verkstedarbeid (f.eks. grunnprepp) føres på servicen når varene kobles til en, så
            verksted og butikksalg holdes adskilt i statistikken (src/lib/butikkvarer.js). */}
        {visVerksted && (
          <label htmlFor={`${id}-verksted`} className="inline-flex min-h-[2.5rem] items-center gap-2 text-slate-800">
            <input id={`${id}-verksted`} type="checkbox" checked={Boolean(l.verksted)} disabled={laast}
              onChange={(e) => onEndre('verksted', e.target.checked)} className="h-5 w-5" />
            Verkstedarbeid
          </label>
        )}
        {!laast && (
          <button type="button" onClick={onFjern} className="ml-auto inline-flex min-h-[2.5rem] items-center gap-1 rounded-lg px-3 font-semibold text-red-800 hover:bg-red-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50">
            Fjern<span className="sr-only"> {l.artikkel}</span>
          </button>
        )}
      </div>
    </li>
  );
}

// linjer: lista som redigeres; onEndre(nyListe). laast: bare visning (f.eks. godkjent tilbud).
// visVerksted: av for varer lagt rett på en service (ServiceButikkvarer), der alt er butikkvarer.
// arbeidsliste: arbeid fra prislisten ({ _id, title, price }); null = bare varer.
export default function VareLinjer({ idPrefiks, linjer, onEndre, laast = false, visVerksted = true, arbeidsliste = null, tomTekst = 'Ingen varer ennå. Søk i produktlisten over.' }) {
  const [manuell, setManuell] = useState({ artikkel: '', pris: '', kostpris: '', ean: '' });
  const [eget, setEget] = useState({ artikkel: '', pris: '' });
  const medArbeid = Array.isArray(arbeidsliste);

  // Med arbeid merkes varer som produkt, så de ikke regnes som arbeid på trykket (trykk/linjer.js).
  const leggTil = (linje) => onEndre([...linjer, regnPaNytt({ antall: 1, rabatt: 0, rabattKr: 0, ...(medArbeid && { type: 'produkt' }), ...linje })]);
  const leggTilArbeid = (artikkel, pris) => leggTil({ type: 'arbeid', artikkel, pris });
  const fraArbeidsliste = (verdi) => {
    const a = arbeidsliste.find((x) => String(x._id) === String(verdi));
    if (a) leggTilArbeid(a.title, Number(a.price) || 0);
  };
  const leggTilEget = () => {
    if (!eget.artikkel.trim() || eget.pris === '') return;
    leggTilArbeid(eget.artikkel.trim(), eget.pris);
    setEget({ artikkel: '', pris: '' });
  };

  const endre = (i, felt, verdi) => onEndre(linjer.map((l, j) => {
    if (j !== i) return l;
    if (felt === 'rabatt') return medRabatt(l, 'prosent', verdi);
    if (felt === 'rabattKr') return medRabatt(l, 'kr', verdi);
    if (felt === 'pris' || felt === 'antall') return regnPaNytt({ ...l, [felt]: verdi });
    return { ...l, [felt]: verdi };
  }));

  const fraProduktliste = ({ navn, variant, storrelse, produktId }) => leggTil({
    artikkel: [navn, storrelse && `str. ${storrelse}`].filter(Boolean).join(', '),
    produktId: produktId || '',
    variantId: variant.id || '',
    ean: variant.ean || '',
    pris: variant.pris ?? '',
    kostpris: variant.kostpris ?? '',
  });

  const leggTilManuelt = () => {
    if (!manuell.artikkel.trim() || manuell.pris === '') return;
    leggTil({ artikkel: manuell.artikkel.trim(), pris: manuell.pris, kostpris: manuell.kostpris, ...(manuell.ean && { ean: manuell.ean }) });
    setManuell({ artikkel: '', pris: '', kostpris: '', ean: '' });
  };

  return (
    <div className="space-y-2">
      {!laast && (
        <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-2.5 sm:p-3">
          <ProduktSok id={`${idPrefiks}-produkt`} onVelg={fraProduktliste} visLager />
          <details className="rounded-lg">
            <summary className="cursor-pointer font-semibold text-indigo-800">Varen finnes ikke i produktlisten</summary>
            <div className="mt-3 flex flex-wrap items-end gap-2">
              <div className="min-w-0 flex-1 basis-56">
                <Tekstfelt id={`${idPrefiks}-man-art`} etikett="Vare" value={manuell.artikkel} onChange={(e) => setManuell((m) => ({ ...m, artikkel: e.target.value }))} />
              </div>
              <Tall id={`${idPrefiks}-man-pris`} etikett="Pris inkl. mva" enhet="kr" value={manuell.pris} onChange={(v) => setManuell((m) => ({ ...m, pris: v }))} />
              <Tall id={`${idPrefiks}-man-kost`} etikett="Kostpris eks. mva" enhet="kr" value={manuell.kostpris} onChange={(v) => setManuell((m) => ({ ...m, kostpris: v }))} />
              <div className="min-w-0">
                <label htmlFor={`${idPrefiks}-man-ean`} className="mb-1 block text-sm font-medium text-slate-700">EAN eller PLU (valgfritt)</label>
                <input id={`${idPrefiks}-man-ean`} type="text" autoComplete="off" value={manuell.ean}
                  onChange={(e) => setManuell((m) => ({ ...m, ean: renEan(e.target.value) }))}
                  className="w-52 max-w-full rounded-lg border-2 border-slate-300 bg-white px-3 py-1.5 font-mono focus:border-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-600/30" />
              </div>
              <button type="button" onClick={leggTilManuelt} disabled={!manuell.artikkel.trim() || manuell.pris === ''} className={knappSekundar}>
                <Ikon navn="pluss" className="w-5 h-5" /> Legg til
              </button>
            </div>
          </details>
        </div>
      )}
      {!laast && medArbeid && (
        <div className="space-y-3 rounded-xl border border-violet-200 bg-violet-50 p-2.5 sm:p-3">
          <p className="font-semibold text-slate-900">Trykk og annet arbeid</p>
          {arbeidsliste.length > 0 && (
            <Valgfelt id={`${idPrefiks}-arbeid`} etikett="Legg til arbeid fra prislisten" value="" onChange={(e) => fraArbeidsliste(e.target.value)}>
              <option value="">Velg trykk eller arbeid</option>
              {arbeidsliste.map((a) => <option key={a._id} value={a._id}>{a.title} – {kr(a.price)}</option>)}
            </Valgfelt>
          )}
          <details className="rounded-lg">
            <summary className="cursor-pointer font-semibold text-indigo-800">Eget arbeid</summary>
            <div className="mt-3 flex flex-wrap items-end gap-2">
              <div className="min-w-0 flex-1 basis-56">
                <Tekstfelt id={`${idPrefiks}-eget-art`} etikett="Arbeid" value={eget.artikkel} onChange={(e) => setEget((m) => ({ ...m, artikkel: e.target.value }))} />
              </div>
              <Tall id={`${idPrefiks}-eget-pris`} etikett="Pris inkl. mva" enhet="kr" value={eget.pris} onChange={(v) => setEget((m) => ({ ...m, pris: v }))} />
              <button type="button" onClick={leggTilEget} disabled={!eget.artikkel.trim() || eget.pris === ''} className={knappSekundar}>
                <Ikon navn="pluss" className="w-5 h-5" /> Legg til
              </button>
            </div>
          </details>
        </div>
      )}
      {linjer.length === 0 ? (
        <p className="rounded-xl border border-dashed border-slate-300 px-3 py-3 text-slate-700">{tomTekst}</p>
      ) : (
        <ul className="space-y-2">
          {linjer.map((l, i) => (
            <Linje key={i} l={l} id={`${idPrefiks === 'tb' ? 'tl' : `${idPrefiks}-l`}${i}`} laast={laast} visVerksted={visVerksted}
              onEndre={(felt, verdi) => endre(i, felt, verdi)}
              onFjern={() => onEndre(linjer.filter((_, j) => j !== i))} />
          ))}
        </ul>
      )}
    </div>
  );
}
